import PropTypes from 'prop-types';
import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';

import Util from '$utilities';

/* ========================================================================= */
/* Constants */
/* ========================================================================= */

// Represents the distance from the display label to the top of the viewport
const TOP_THRESHOLD = 250 + 60; // mobile navigation section height + small arbitrary offset
const TOP_THRESHOLD_DESKTOP = 60;

/* ========================================================================= */
/* Context */
/* ========================================================================= */

const SideTitlesContext = createContext({ current: null });

SideTitlesContext.displayName = 'SideTitlesContext';

/* ========================================================================= */
/* Providers(s) */
/* ========================================================================= */

const SideTitlesProvider = (props) => {
    // references
    const requestAnimationFrameRef = useRef();

    // state
    const [current, setCurrent] = useState();
    const [staticTitles, setStaticTitles] = useState({});

    // methods
    const addStaticTitle = (title, ref) => {
        const key = Util.getId();

        setStaticTitles((previousTitles) => ({
            ...previousTitles,
            [key]: { title, ref }
        }));

        return key;
    };

    const removeStaticTitle = (key) => {
        if (key && key in staticTitles) {
            setStaticTitles((previousTitles) => {
                let newTitles = { ...previousTitles };
                delete newTitles[key];
                return newTitles;
            });
        }
    };

    const scrollFrame = () => {
        let distance = null;

        Object.keys(staticTitles).forEach((key) => {
            const element = staticTitles[key];

            if (!element?.ref?.current) { return; }

            const rect = element.ref.current.getBoundingClientRect();
            const threshold = document.documentElement.clientWidth < 1024 ? TOP_THRESHOLD : TOP_THRESHOLD_DESKTOP;

            if (rect.top <= threshold && (!distance || rect.top > distance)) {
                distance = rect.top;
                setCurrent(key);
            }
        });

        if (Object.keys(staticTitles).length > 0 && distance === null) {
            setCurrent(null);
        }

        requestAnimationFrameRef.current = window.requestAnimationFrame(scrollFrame);
    };

    // hooks
    useEffect(() => {
        requestAnimationFrameRef.current = window.requestAnimationFrame(scrollFrame);

        return () => window.cancelAnimationFrame(requestAnimationFrameRef.current);
    }, [ staticTitles ]);

    // render
    return (
        <SideTitlesContext.Provider
            value={{
                current,
                addStaticTitle,
                removeStaticTitle,
                title: current ? staticTitles[current]?.title : null
            }}
            {...props}>
            {props.children}
        </SideTitlesContext.Provider>
    );
};

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

const useSideTitles = () => {
    const context = useContext(SideTitlesContext);

    if (!context) {
        throw new Error('useSideTitles hook must be used within a SideTitlesContext.Provider component');
    }

    return context;
};

/* ========================================================================= */
/* Typing & export(s) */
/* ========================================================================= */

SideTitlesProvider.propTypes = { children: PropTypes.any };

export {
    SideTitlesProvider,
    useSideTitles
};
